const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatValue = (value, unit = '', maximumFractionDigits = 1, minimumFractionDigits = 0) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }

    const unitSeparator = unit && !unit.startsWith('/') ? ' ' : '';

    return `${new Intl.NumberFormat('pl-PL', { minimumFractionDigits, maximumFractionDigits }).format(Number(value))}${unit ? `${unitSeparator}${unit}` : ''}`;
};

const show = (element, visible) => element?.classList.toggle('d-none', !visible);

const initMarketRadar = (root) => {
    if (!root || root.dataset.initialized === 'true') {
        return;
    }

    root.dataset.initialized = 'true';
    root._marketRadarRequest = null;
    loadMarketRadar(root);
};

const loadMarketRadar = async (root) => {
    const loading = root.querySelector('[data-market-radar-loading]');
    const error = root.querySelector('[data-market-radar-error]');
    const empty = root.querySelector('[data-market-radar-empty]');
    const content = root.querySelector('[data-market-radar-content]');
    const scope = root.querySelector('[data-market-radar-scope]')?.value || 'nearby';
    const period = root.querySelector('[data-market-radar-period]')?.value || '90';

    root._marketRadarRequest?.abort();
    root._marketRadarRequest = new AbortController();
    show(loading, true);
    show(error, false);
    show(empty, false);
    show(content, false);

    try {
        const url = new URL(root.dataset.endpoint, window.location.origin);
        url.searchParams.set('scope', scope);
        url.searchParams.set('period', period);
        const response = await fetch(url, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            signal: root._marketRadarRequest.signal,
        });

        if (!response.ok) {
            throw new Error(`Market radar request failed with ${response.status}`);
        }

        const report = await response.json();
        show(loading, false);

        if (report.status !== 'ready') {
            renderEmptyState(root, report);
            show(empty, true);
            return;
        }

        renderReadyState(root, report);
        show(content, true);
    } catch (requestError) {
        if (requestError.name === 'AbortError') {
            return;
        }

        show(loading, false);
        show(error, true);
    }
};

const renderEmptyState = (root, report) => {
    const target = root.querySelector('[data-market-radar-empty]');
    const progress = report.progress;
    const statusIcon = report.status === 'building' ? 'fa-gears' : report.status === 'no_cohort' ? 'fa-users-slash' : 'fa-seedling';
    const title = report.status === 'building'
        ? 'Budujemy Twój Radar Rynku'
        : report.status === 'no_cohort'
            ? 'Grupa porównawcza jeszcze rośnie'
            : 'Zbieramy sygnały rynkowe';
    const progressHtml = progress ? `
        <div class="market-radar__progress mt-4">
            <div class="d-flex justify-content-between small fw-bold mb-2">
                <span>Postęp</span>
                <span>${escapeHtml(progress.current)} / ${escapeHtml(progress.required)}</span>
            </div>
            <div class="progress" role="progressbar" aria-valuenow="${escapeHtml(progress.percent)}" aria-valuemin="0" aria-valuemax="100">
                <div class="progress-bar bg-secondary" style="width: ${Number(progress.percent) || 0}%"></div>
            </div>
        </div>` : '';
    const criteriaHtml = report.cohort?.criteria?.length
        ? `<div class="mt-3">${report.cohort.criteria.map((criterion) => `<span class="badge text-bg-light border me-1 mb-1">${escapeHtml(criterion)}</span>`).join('')}</div>`
        : '';
    const tipsHtml = report.setup_tips?.length
        ? `<ul class="list-group list-group-flush mt-4">${report.setup_tips.map((tip) => `<li class="list-group-item bg-transparent px-0"><i class="fa-regular fa-circle-check text-success me-2"></i>${escapeHtml(tip)}</li>`).join('')}</ul>`
        : '';

    target.innerHTML = `
        <section class="market-radar__empty-card mb-4">
            <div class="market-radar__empty-icon"><i class="fad ${statusIcon}"></i></div>
            <div>
                <div class="text-uppercase small fw-bold text-primary mb-2">Radar Rynku</div>
                <h2 class="h3 mb-2">${title}</h2>
                <p class="text-body-secondary mb-0">${escapeHtml(report.message || 'Potrzebujemy jeszcze kilku sygnałów, aby pokazać wiarygodny wynik.')}</p>
                ${criteriaHtml}
                ${progressHtml}
                ${tipsHtml}
            </div>
        </section>
        ${renderCompetitorSection(report.competitors || [], true)}`;
};

const renderReadyState = (root, report) => {
    renderHero(root, report);
    renderRadar(root, report.pillars || []);
    renderPillars(root, report.pillars || []);
    renderMetrics(root, report.metrics || []);
    renderHeatmap(root, report.heatmap || {});
    renderRecommendations(root, report.recommendations || []);
    root.querySelector('[data-market-radar-competitors]').innerHTML = renderCompetitorCards(report.competitors || []);
};

const renderHero = (root, report) => {
    const generatedAt = report.generated_at
        ? new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(report.generated_at))
        : '—';
    const criteria = report.cohort.criteria
        .map((criterion) => `<span class="market-radar__criterion">${escapeHtml(criterion)}</span>`)
        .join('');

    root.querySelector('[data-market-radar-hero]').innerHTML = `
        <div class="market-radar__score">
            <span>${escapeHtml(Math.round(report.score))}</span><small>/100</small>
        </div>
        <div class="market-radar__hero-copy">
            <div class="market-radar__eyebrow">Twoja pozycja rynkowa</div>
            <h2>${escapeHtml(report.position_text)}</h2>
            <p>${escapeHtml(report.cohort.description)} · anonimowa grupa ${escapeHtml(report.cohort.size)} restauracji</p>
            <div class="market-radar__criteria">${criteria}</div>
        </div>
        <div class="market-radar__confidence">
            <span class="market-radar__confidence-label">Wiarygodność</span>
            <strong>${escapeHtml(report.confidence.label)}</strong>
            <span>${escapeHtml(report.confidence.score)}%</span>
            <small>Aktualizacja: ${escapeHtml(generatedAt)}</small>
        </div>`;
};

const renderRadar = (root, pillars) => {
    const canvas = root.querySelector('[data-market-radar-radar]');

    if (!canvas || typeof window.Chart === 'undefined') {
        return;
    }

    root._marketRadar?.destroy();
    root._marketRadar = new window.Chart(canvas, {
        type: 'radar',
        data: {
            labels: pillars.map((pillar) => pillar.label),
            datasets: [
                {
                    label: 'Ty',
                    data: pillars.map((pillar) => pillar.score ?? 0),
                    borderColor: '#ffcc00',
                    backgroundColor: 'rgba(255, 204, 0, .24)',
                    borderWidth: 3,
                    pointBackgroundColor: '#ffcc00',
                },
                {
                    label: 'Mediana rynku',
                    data: pillars.map(() => 50),
                    borderColor: '#6c757d',
                    backgroundColor: 'rgba(108, 117, 125, .04)',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'Top 25%',
                    data: pillars.map(() => 75),
                    borderColor: '#20c997',
                    backgroundColor: 'rgba(32, 201, 151, .03)',
                    borderDash: [2, 4],
                    pointRadius: 0,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                r: {
                    min: 0,
                    max: 100,
                    ticks: { display: false, stepSize: 25 },
                    pointLabels: { font: { size: 13, weight: 600 } },
                    grid: { color: 'rgba(128, 128, 128, .18)' },
                },
            },
            plugins: { legend: { position: 'bottom' } },
        },
    });
};

const renderPillars = (root, pillars) => {
    root.querySelector('[data-market-radar-pillars]').innerHTML = pillars.map((pillar) => `
        <div class="market-radar__pillar">
            <div class="d-flex justify-content-between align-items-center gap-2">
                <span class="fw-bold">${escapeHtml(pillar.label)}</span>
                <span class="fw-bold">${pillar.score === null ? '—' : `${escapeHtml(Math.round(pillar.score))}/100`}</span>
            </div>
            <div class="progress mt-2" role="progressbar" aria-valuenow="${escapeHtml(pillar.score || 0)}" aria-valuemin="0" aria-valuemax="100">
                <div class="progress-bar" style="width: ${Number(pillar.score) || 0}%"></div>
            </div>
            <small class="text-body-secondary">${pillar.value === null || pillar.value === undefined
                ? 'Za mało danych'
                : escapeHtml(formatValue(pillar.value, pillar.unit, pillar.precision ?? 1, pillar.precision ?? 0))}</small>
        </div>`).join('');
};

const renderMetrics = (root, metrics) => {
    root.querySelector('[data-market-radar-metrics]').innerHTML = metrics.map((metric) => {
        const self = metric.type === 'number' ? formatValue(metric.self, metric.unit) : escapeHtml(metric.self);
        const median = metric.type === 'number' ? formatValue(metric.median, metric.unit) : escapeHtml(metric.median);
        const top = metric.type === 'number' ? formatValue(metric.top_quartile, metric.unit) : '—';

        return `<div class="col-md-6">
            <article class="market-radar__metric-card h-100">
                <h3>${escapeHtml(metric.label)}</h3>
                <strong>${self}</strong>
                <div class="market-radar__metric-comparison">
                    <span><small>Mediana</small>${median}</span>
                    <span><small>Top 25%</small>${top}</span>
                </div>
            </article>
        </div>`;
    }).join('');
};

const renderHeatmap = (root, heatmap) => {
    const target = root.querySelector('[data-market-radar-heatmap]');
    const hours = heatmap.hours || [];
    const cells = new Map((heatmap.cells || []).map((cell) => [`${cell.day}-${cell.hour}`, cell]));
    target.style.setProperty('--market-radar-hour-count', String(Math.max(1, hours.length)));
    let html = '<div class="market-radar__heatmap-corner"></div>';
    html += hours.map((hour) => `<div class="market-radar__heatmap-hour">${String(hour).padStart(2, '0')}</div>`).join('');

    Object.entries(heatmap.days || {}).forEach(([day, dayLabel]) => {
        html += `<div class="market-radar__heatmap-day">${escapeHtml(dayLabel)}</div>`;
        hours.forEach((hour) => {
            const cell = cells.get(`${day}-${hour}`) || { own_share: 0, market_share: 0, gap: 0 };
            const status = cell.gap > 0.35 ? 'opportunity' : cell.gap < -0.35 ? 'advantage' : 'neutral';
            const intensity = Math.min(1, Math.abs(Number(cell.gap)) / 5).toFixed(2);
            const title = `Ty: ${formatValue(cell.own_share, '%')} · rynek: ${formatValue(cell.market_share, '%')}`;
            html += `<div class="market-radar__heatmap-cell market-radar__heatmap-cell--${status}" style="--heat: ${intensity}" title="${escapeHtml(title)}"><span>${cell.gap > 0.35 ? '+' : ''}${escapeHtml(Number(cell.gap).toFixed(1))}</span></div>`;
        });
    });

    target.innerHTML = html;
};

const renderRecommendations = (root, recommendations) => {
    const target = root.querySelector('[data-market-radar-recommendations]');

    if (!recommendations.length) {
        target.innerHTML = '<div class="col-12"><div class="alert alert-success mb-0"><i class="fa-regular fa-circle-check me-2"></i>Nie znaleźliśmy dziś istotnej luki. Utrzymuj aktualne ustawienia i wróć po kolejnym przeliczeniu.</div></div>';
        return;
    }

    target.innerHTML = recommendations.map((recommendation, index) => {
        return `<div class="col-lg-4">
            <article class="market-radar__recommendation h-100">
                <div class="market-radar__recommendation-number">0${index + 1}</div>
                <h3>${escapeHtml(recommendation.title)}</h3>
                <p>${escapeHtml(recommendation.description)}</p>
                ${recommendation.disclaimer ? `<small>${escapeHtml(recommendation.disclaimer)}</small>` : ''}
                <a class="btn btn-dark mt-auto" href="${escapeHtml(recommendation.action_target)}" data-open-nested-tab-href="${escapeHtml(recommendation.action_target)}">${escapeHtml(recommendation.action_label)} <i class="fa-regular fa-arrow-right ms-2"></i></a>
            </article>
        </div>`;
    }).join('');
};

const renderCompetitorCards = (competitors) => {
    if (!competitors.length) {
        return '<div class="col-12"><p class="text-body-secondary mb-0">Brak publicznych restauracji spełniających kryteria w pobliżu.</p></div>';
    }

    return competitors.map((competitor) => {
        const cuisines = competitor.cuisines?.length ? competitor.cuisines.join(' · ') : 'Kuchnia nieokreślona';
        const distance = competitor.distance_km !== null ? `${formatValue(competitor.distance_km, 'km')}` : 'Ta sama miejscowość';
        const zjedzRating = competitor.zjedz_rating !== null ? `${formatValue(competitor.zjedz_rating)}/5 (${escapeHtml(competitor.zjedz_rating_count)})` : '—';
        const googleRating = competitor.google_rating !== null ? `${formatValue(competitor.google_rating)}/5 (${escapeHtml(competitor.google_rating_count || 0)})` : '—';

        return `<div class="col-md-6 col-xl-4">
            <article class="market-radar__competitor h-100">
                <div>
                    <span class="small text-body-secondary"><i class="fa-regular fa-location-dot me-1"></i>${escapeHtml(distance)}</span>
                    <h3 class="h5 mt-1 mb-1"><a href="${escapeHtml(competitor.url)}" target="_blank" rel="noopener">${escapeHtml(competitor.name)}</a></h3>
                    <p class="small text-body-secondary mb-0">${escapeHtml(cuisines)}</p>
                </div>
                <div class="market-radar__competitor-stats">
                    <span><small>Zjedz.my</small><strong>${zjedzRating}</strong></span>
                    <span><small>Google</small><strong>${googleRating}</strong></span>
                    <span><small>Ceny</small><strong>${competitor.price_tier ? Array(Math.min(5, competitor.price_tier)).fill('$').join(' ') : '—'}</strong></span>
                </div>
            </article>
        </div>`;
    }).join('');
};

const renderCompetitorSection = (competitors, compact = false) => `
    <section class="card border-0 shadow-sm mb-4 ${compact ? 'market-radar__public-landscape' : ''}">
        <div class="card-body p-4">
            <h2 class="h4 mb-1">Publiczny krajobraz konkurencji</h2>
            <p class="text-body-secondary mb-4">Te dane są publiczne i nie zawierają wyników operacyjnych innych lokali.</p>
            <div class="row g-3">${renderCompetitorCards(competitors)}</div>
        </div>
    </section>`;

$(document).ready(() => {
    document.querySelectorAll('[data-market-radar]').forEach(initMarketRadar);
});

$(document).on('shown.bs.tab', '#company-market-radar, [href="#company-market-radar"]', () => {
    document.querySelectorAll('[data-market-radar]').forEach(initMarketRadar);
});

$(document).on('change', '[data-market-radar-scope], [data-market-radar-period]', function () {
    loadMarketRadar(this.closest('[data-market-radar]'));
});

$(document).on('click', '[data-market-radar-retry]', function () {
    loadMarketRadar(this.closest('[data-market-radar]'));
});
